import React, { useEffect, useMemo, useState } from 'react'
import { Button, Box, IconButton, TextField } from '@mui/material'

import { Close } from '@mui/icons-material'
import { authedAxios, debounce } from '../utils'
import WizardFooter from '../setupWizard/shared/WizardFooter'
import SettingsFooter from './shared/SettingsFooter'
import useAlert from '../hooks/useAlert'
import useGenericStoreEndpoint from '../hooks/api/useGenericStoreEndpoint'
import { COMPANY_ACTIONS } from '../store/company'

const LogoForm = ({ wizardFooterProps, state, updateState }) => {
  let inputRef

  const { logoSrc = '', selectedFile = null, company = null } = state

  const hasInitializedData = useMemo(
    () => ({
      logoSrc: !!state.logoSrc,
      selectedFile: !!state.selectedFile,
      company: !!state.company,
    }),
    []
  )

  const setSelectedFile = (selectedFile) => updateState({ selectedFile })
  const setLogoSrc = (logoSrc) => updateState({ logoSrc })
  const setCompany = (company) => updateState({ company })

  const [isDirty, setIsDirty] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)

  const { createAlert, createGenericErrorAlert } = useAlert()

  const getCompanyLogo = debounce((companyId) => {
    return authedAxios
      .get(`/api/image/company-${companyId}-company-logo`)
      .then(({ data }) => {
        setHasFetched(true)
        setLogoSrc(data)
      })
      .catch(() => {})
  }, 100)

  useEffect(() => {
    if (
      !company ||
      hasFetched ||
      hasInitializedData.logoSrc ||
      hasInitializedData.selectedFile
    )
      return

    getCompanyLogo(company.id)
  }, [company, hasInitializedData, hasFetched])

  const {
    localData: companyData,
    isLoading,
    hasLoadError,
    setLocalData,
    commitToStore,
  } = useGenericStoreEndpoint(
    '/api/company',
    COMPANY_ACTIONS.SET_COMPANY,
    'company'
  )

  useEffect(() => {
    if (hasInitializedData.company) return
    setCompany(companyData)
  }, [companyData])

  const setImage = (logoSrc) => {
    setLogoSrc(logoSrc)
  }

  const handleSelectFile = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0])
      const reader = new FileReader()

      reader.onload = (e) => {
        setImage(e.target.result)
      }

      reader.readAsDataURL(e.target.files[0])
    }
  }

  const handleFileUpload = () => {
    if (logoSrc && !selectedFile) return Promise.resolve()

    const formData = new FormData()
    formData.append('logo', selectedFile)

    return authedAxios.put('/api/registration/logo', formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    })
  }

  const handleSave = () => {
    return authedAxios
      .put('/api/company', { name: company?.name })
      .then(commitToStore)
      .then(() => {
        if (!logoSrc) return Promise.resolve()
        return handleFileUpload()
      })
  }

  const onSuccess = () =>
    createAlert({
      message: 'Name and logo saved successfully',
      severity: 'success',
    })

  const onFailure = () => createGenericErrorAlert()

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <TextField
        sx={{ mb: 2 }}
        label={'Company Name'}
        value={company?.name || ''}
        onChange={(e) => {
          const newVal = { ...company, name: e.target.value }
          setIsDirty(true)
          setLocalData(newVal)
          setCompany(newVal)
        }}
      ></TextField>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 2,
          alignSelf: 'center',
        }}
      >
        {logoSrc && (
          <IconButton
            onClick={() => {
              setImage(null)
              setSelectedFile(null)
              inputRef.value = null
            }}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              backgroundColor: 'white',
              boxShadow: '0px 1px 4px 1px rgb(0 0 0 / 20%)',
              ':hover': {
                backgroundColor: '#e7eaeb',
              },
            }}
          >
            <Close />
          </IconButton>
        )}
        {logoSrc ? (
          <img
            style={{
              width: '300px',
              height: '300px',
              objectFit: 'contain',
              backgroundColor: '#efefef',
              boxShadow: '0px 1px 4px 1px rgb(0 0 0 / 20%)',
            }}
            src={logoSrc}
            alt='uploaded-img'
          />
        ) : (
          <Box
            sx={{
              width: '300px',
              height: '300px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button variant={'contained'} onClick={() => inputRef.click()}>
              Click to upload a logo
            </Button>
          </Box>
        )}
      </Box>

      <input
        hidden
        type='file'
        onChange={handleSelectFile}
        ref={(fileInputRef) => (inputRef = fileInputRef)}
      />

      {wizardFooterProps && (
        <WizardFooter
          {...wizardFooterProps}
          primaryButton={{
            ...wizardFooterProps.primaryButton,
            disabled: !logoSrc,
            onClick: () => {
              handleSave()
                .then(() => {
                  wizardFooterProps.primaryButton?.onClick?.()
                })
                .catch(onFailure)
            },
          }}
        />
      )}

      {!wizardFooterProps && (
        <SettingsFooter
          cancelButton={{
            onClick: () => {
              setSelectedFile(null)
              getCompanyLogo()
            },
          }}
          saveButton={{
            onClick: () => handleSave().then(onSuccess).catch(onFailure),
            disabled: !logoSrc && !isDirty,
          }}
        />
      )}
    </Box>
  )
}

export default LogoForm
